import React, { useState, useEffect } from 'react';
import { UserProvider } from './context/UserContext'; // Import the UserProvider
import api from './services/api'; // Importa o serviço de API configurado
import LandingPage from './components/LandingPage';
import HomePage from './components/HomePage';
import CatalogoPage from './components/CatalogoPage';
import TopicosPage from './components/TopicosPage';
import SubtopicoPage from './components/SubtopicoPage';
import ProgressoPage from './components/ProgressoPage';
import FlashcardsPage from './components/FlashcardsPage';
import FlashcardRevisionPage from './components/FlashcardRevisionPage';
import FlashcardsBySubjectPage from './components/FlashcardsBySubjectPage';
import ConectaPage from './components/ConectaPage';
import AdminPage from './components/AdminPage';
import PoliticaPrivacidade from './components/PoliticaPrivacidade';
import NewStorePage from './components/NewStorePage'; // Importar novo componente da loja
import CartPage from './components/CartPage'; // Importar componente do carrinho
import ProductDetailPage from './components/ProductDetailPage'; // Importar componente de detalhes do produto
import CoursePlayerPage from './components/CoursePlayerPage'; // Importar componente do player de curso
import CronogramaPersonalizadoPage from './components/CronogramaPersonalizadoPage'; // Importar componente do cronograma personalizado
import IndiqueGanhePage from './components/IndiqueGanhePage'; // Importar componente de indique e ganhe
import RewardsStorePage from './components/RewardsStorePage'; // Importar componente da loja de recompensas
import VendaPremiumPage from './components/VendaPremiumPage'; // Importar componente da página de vendas premium
import { CartProvider } from './context/CartContext'; // Importar contexto do carrinho
import { trackPageView, sendGTMEvent } from './utils/gtm'; // Importar funções do GTM
import RegistrationRequestsPage from './components/RegistrationRequestsPage'; // Importar componente de gerenciamento de solicitações de registro
import './App.css';

// Componente para editar o nome do usuário
const EditNameModal = ({ user, token, onClose, onUpdate }) => {
  const [nome, setNome] = useState(user?.nome || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await api.put('/api/users/profile', { nome }, config);

      // Atualizar os dados do usuário
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Erro ao atualizar nome:', error);
      setError(error.response?.data?.message || 'Erro ao atualizar nome');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Editar Nome</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Novo Nome
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#9B4DFF] text-white font-semibold rounded-lg hover:bg-[#7B2CDB] transition-colors disabled:bg-purple-300"
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Componente para alterar a senha do usuário
const ChangePasswordModal = ({ token, onClose }) => {
  const [formData, setFormData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validações
    if (formData.novaSenha.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    if (formData.novaSenha !== formData.confirmarSenha) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      await api.put('/api/users/change-password', {
        senhaAtual: formData.senhaAtual,
        novaSenha: formData.novaSenha
      }, config);

      setSuccess('Senha alterada com sucesso!');
      // Limpar campos após sucesso
      setFormData({
        senhaAtual: '',
        novaSenha: '',
        confirmarSenha: ''
      });

      // Fechar modal após 2 segundos
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setError(error.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Alterar Senha</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl"
            disabled={loading}
          >
            &times;
          </button>
        </div>

        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senha Atual
            </label>
            <input
              type="password"
              name="senhaAtual"
              value={formData.senhaAtual}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nova Senha
            </label>
            <input
              type="password"
              name="novaSenha"
              value={formData.novaSenha}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500"
              required
              minLength="6"
            />
            <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Nova Senha
            </label>
            <input
              type="password"
              name="confirmarSenha"
              value={formData.confirmarSenha}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#9B4DFF] text-white font-semibold rounded-lg hover:bg-[#7B2CDB] transition-colors disabled:bg-purple-300"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckPerformed, setAdminCheckPerformed] = useState(false);

  const [currentPage, setCurrentPage] = useState('home');
  const [currentParams, setCurrentParams] = useState(null);
  const [selectedMateria, setSelectedMateria] = useState({ id: null, nome: null });
  const [selectedTopico, setSelectedTopico] = useState({ id: null, nome: null });
  const [selectedSubtopico, setSelectedSubtopico] = useState({ id: null, nome: null });

  // Estados para os modais
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  useEffect(() => {
    const userInfoFromStorage = localStorage.getItem('userInfo');

    if (userInfoFromStorage) {
      const parsedUserInfo = JSON.parse(userInfoFromStorage);
      setUser(parsedUserInfo);
      setToken(parsedUserInfo.token);
      setIsLoggedIn(true);

      // Verificar se o usuário já foi verificado como admin anteriormente
      const isAdminVerified = localStorage.getItem('isAdminVerified') === 'true';
      setIsAdmin(isAdminVerified);
    }
  }, []);

  // Função para mapear páginas para URLs
  const getPageToUrlMap = () => {
    return {
      'home': '/',
      'catalogo': '/catalogo',
      'topicos': '/topicos',
      'subtopico': '/subtopico',
      'progresso': '/progresso',
      'flashcards': '/flashcards',
      'flashcard-revision': '/flashcard-revision',
      'flashcards-by-subject': '/flashcards-by-subject',
      'conecta': '/conecta',
      'admin': '/admin',
      'token': '/token',
      'perfil': '/perfil',
      'politica-privacidade': '/politica-privacidade',
      'cart': '/loja/carrinho',
      'product-detail': '/produto',
      'cronograma-personalizado': '/cronograma-personalizado',
      'indique-ganhe': '/indique-e-ganhe',
      'rewards-store': '/loja-de-recompensa',
      'venda-premium': '/venda-premium',
    };
  };

  // Função para atualizar a URL quando a página muda
  const navigateWithUrl = (page, params = null) => {
    setCurrentPage(page);
    setCurrentParams(params); // Add this to save parameters

    // Mapear páginas personalizadas para URLs
    let url;
    if (page === 'indique-e-ganhe') {
      url = '/indique-e-ganhe';
    } else if (page === 'loja-de-recompensa') {
      url = '/loja-de-recompensa';
    } else {
      const pageToUrl = getPageToUrlMap();
      url = pageToUrl[page] || '/';
    }

    // Adicionar parâmetros à URL se fornecidos
    if (params && params.id != null && params.id !== '') {
      url += `/${encodeURIComponent(params.id)}`;
    }

    // Garantir que a URL seja relativa e não cause problemas de segurança
    if (!url.startsWith('/')) {
      url = '/' + url;
    }

    // Garantir que a URL não seja vazia
    if (!url || url === '/') {
      url = '/';
    } else {
      // Remover qualquer caractere especial que possa interferir
      url = url.replace(/[^a-zA-Z0-9/_\-]/g, '');
    }

    // Atualizar a URL sem recarregar a página
    try {
      // Garantir que a URL seja sempre relativa e válida
      let finalUrl = url || '/';
      if (!finalUrl.startsWith('/')) {
        finalUrl = '/' + finalUrl;
      }

      // Evitar URLs que possam causar problemas de segurança
      if (finalUrl.includes('//')) {
        finalUrl = finalUrl.replace(/\/+/g, '/');
      }

      window.history.pushState({}, '', finalUrl);

      // Rastrear a mudança de página no GTM
      trackPageView(finalUrl, page);

      // Rolar para o topo da página após a navegação
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Erro ao atualizar a URL:', error);
      // Fallback: navegar para a página base
      window.history.pushState({}, '', '/');
      // Rolar para o topo da página mesmo em caso de erro
      window.scrollTo(0, 0);
    }
  };

  // Efeito para atualizar a página quando a URL mudar
  useEffect(() => {
    const handlePopState = () => {
      const pathname = window.location.pathname;
      let page = 'home';
      let pageId = null;

      // Verificar se é uma URL de detalhes de produto
      if (pathname.startsWith('/produto/')) {
        page = 'product-detail';
        pageId = pathname.split('/')[2]; // Pegar o ID do produto
        setCurrentParams({ id: pageId });
      }
      // Verificar se é uma URL de classroom (classroom/:courseId/:lessonId)
      else if (pathname.startsWith('/classroom/')) {
        const parts = pathname.split('/');
        if (parts.length >= 4) { // /classroom/:courseId/:lessonId
          page = 'classroom';
          const courseId = parts[2];
          const lessonId = parts[3];
          setCurrentParams({ courseId, lessonId });
        } else if (parts.length === 3) { // /classroom/:courseId (without lessonId)
          page = 'classroom';
          const courseId = parts[2];
          setCurrentParams({ courseId });
        } else {
          page = 'classroom';
          setCurrentParams({});
        }
      }

      const pageToUrl = getPageToUrlMap();
      const urlToPage = {};

      // Inverter o mapeamento para encontrar a página correspondente à URL
      Object.keys(pageToUrl).forEach(p => {
        urlToPage[pageToUrl[p]] = p;
      });

      page = urlToPage[pathname] || 'home';

      // Mapear URLs personalizadas para páginas internas
      if (pathname === '/indique-e-ganhe') {
        page = 'indique-e-ganhe';
      } else if (pathname === '/loja-de-recompensa') {
        page = 'rewards-store';
      }

      setCurrentPage(page);

      // Rastrear a mudança de página no GTM
      trackPageView(pathname, page);
    };

    // Adicionar listener para mudanças de URL (botão de voltar/avançar)
    window.addEventListener('popstate', handlePopState);

    // Definir página inicial baseada na URL atual
    const pathname = window.location.pathname;
    let initialPage = 'home';
    let pageId = null;

    // Verificar se é uma URL de detalhes de produto
    if (pathname.startsWith('/produto/')) {
      initialPage = 'product-detail';
      pageId = pathname.split('/')[2]; // Pegar o ID do produto
      setCurrentParams({ id: pageId });
    }
    // Verificar se é uma URL de classroom (classroom/:courseId/:lessonId)
    else if (pathname.startsWith('/classroom/')) {
      const parts = pathname.split('/');
      if (parts.length >= 4) { // /classroom/:courseId/:lessonId
        initialPage = 'classroom';
        const courseId = parts[2];
        const lessonId = parts[3];
        setCurrentParams({ courseId, lessonId });
      } else if (parts.length === 3) { // /classroom/:courseId (without lessonId)
        initialPage = 'classroom';
        const courseId = parts[2];
        setCurrentParams({ courseId });
      } else {
        initialPage = 'classroom';
        setCurrentParams({});
      }
    }

    const urlToPage = {};
    const pageToUrl = getPageToUrlMap();

    Object.keys(pageToUrl).forEach(p => {
      urlToPage[pageToUrl[p]] = p;
    });

    initialPage = urlToPage[pathname] || 'home';

    setCurrentPage(initialPage);

    // Rastrear a página inicial no GTM
    trackPageView(pathname, initialPage);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleLogin = async (userInfo) => {
    // Store user info in localStorage
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    setUser(userInfo);
    setToken(userInfo.token);
    setIsLoggedIn(true);

    // Verificar se o usuário já foi verificado como admin anteriormente
    const isAdminVerified = localStorage.getItem('isAdminVerified') === 'true';
    setIsAdmin(isAdminVerified);
    setCurrentPage('home');

    // Rastrear evento de login no GTM
    sendGTMEvent('login', 'engagement', 'user_login', 1);
  };

  // Efeito para lidar com logout automático por token expirado
  useEffect(() => {
    const handleAutoLogout = () => {
      setUser(null);
      setToken(null);
      setIsLoggedIn(false);
      setIsAdmin(false);
      setCurrentPage('home');
    };

    window.addEventListener('userLoggedOut', handleAutoLogout);

    return () => {
      window.removeEventListener('userLoggedOut', handleAutoLogout);
    };
  }, []);

  const handleLogout = () => {
    // Rastrear evento de logout no GTM
    sendGTMEvent('logout', 'engagement', 'user_logout', 1);

    localStorage.removeItem('userInfo');
    localStorage.removeItem('currentPage');
    setUser(null);
    setToken(null);
    setIsLoggedIn(false);
    setIsAdmin(false);
    setCurrentPage('home');
  };

  const handleToggleAdmin = async () => {
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await api.get('/api/admin/check-admin', config);

      if (response.data.isAdmin) {
        // Atualizar o estado isAdmin para true
        setIsAdmin(true);

        // Armazenar que este usuário é admin para futuras sessões
        localStorage.setItem('isAdminVerified', 'true');

        // Mostrar mensagem de sucesso
        alert('Você agora tem acesso de administrador!');

        // Recarregar a página para que os componentes atualizem com base no novo status de admin
        window.location.reload();
      } else {
        alert('Você não tem permissão para se tornar administrador.');
      }
    } catch (error) {
      console.error('Erro ao verificar permissão de administrador:', error);
      alert('Erro ao verificar permissão de administrador.');
    } finally {
      // Marcar que a verificação foi realizada
      setAdminCheckPerformed(true);
    }
  };

  const navigateToTopicos = (materia) => {
    setSelectedMateria({ id: materia._id, nome: materia.nome });
    setCurrentPage('topicos');
  };

  const navigateToSubtopico = (subtopico) => {
    setSelectedSubtopico({ id: subtopico._id, nome: subtopico.nome });
    setCurrentPage('subtopico');
  };

  const renderPage = () => {
    switch(currentPage) {
      case 'home':
        return <HomePage
                  user={user}
                  token={token}
                  isAdmin={isAdmin}
                  onNavigate={navigateWithUrl}
                  onLogout={handleLogout}
                />;
      case 'catalogo':
        return <CatalogoPage onVoltar={() => navigateWithUrl('home')} onMateriaClick={navigateToTopicos} />;
      case 'topicos':
        return <TopicosPage
                  materiaId={selectedMateria.id}
                  materiaNome={selectedMateria.nome}
                  onVoltar={() => navigateWithUrl('catalogo')}
                  onSubtopicoClick={navigateToSubtopico}
               />;
      case 'subtopico':
        return <SubtopicoPage
                  subtopicoId={selectedSubtopico.id}
                  subtopicoNome={selectedSubtopico.nome}
                  onVoltar={() => navigateWithUrl('topicos')}
                  isAdmin={isAdmin}
               />;
      case 'progresso':
        return <ProgressoPage onVoltar={(page) => page ? navigateWithUrl(page) : navigateWithUrl('home')} user={user} />;
      case 'flashcards':
        return <FlashcardsPage onVoltar={navigateWithUrl} onNavigate={navigateWithUrl} user={user} />;
      case 'flashcard-revision':
        return <FlashcardRevisionPage onVoltar={() => navigateWithUrl('flashcards')} token={token} user={user} />;
      case 'flashcards-by-subject':
        return <FlashcardsBySubjectPage onVoltar={() => navigateWithUrl('flashcards')} onNavigate={navigateWithUrl} user={user} />;
      case 'conecta':
        return <ConectaPage onVoltar={() => navigateWithUrl('home')} token={token} isAdmin={isAdmin} user={user} />;
      case 'admin':
        return <AdminPage
                  onVoltar={() => navigateWithUrl('home')}
                  token={token}
                  user={user}
                  onUpdateUser={(updatedUser) => {
                    // Atualizar o estado do usuário
                    const newUser = { ...user, ...updatedUser };
                    setUser(newUser);
                    // Atualizar também no localStorage
                    localStorage.setItem('userInfo', JSON.stringify(newUser));
                  }}
               />;
      case 'perfil':
        return <div className="min-h-screen bg-[#F8F6FC]">
                  {showEditNameModal && (
                    <EditNameModal
                      user={user}
                      token={token}
                      onClose={() => setShowEditNameModal(false)}
                      onUpdate={(updatedData) => {
                        const updatedUser = { ...user, ...updatedData };
                        setUser(updatedUser);
                        localStorage.setItem('userInfo', JSON.stringify(updatedUser));
                      }}
                    />
                  )}

                  {showChangePasswordModal && (
                    <ChangePasswordModal
                      token={token}
                      onClose={() => setShowChangePasswordModal(false)}
                    />
                  )}

                  <div className="container mx-auto p-4 sm:p-6 md:p-8">
                    <header className="flex items-center justify-between mb-6">
                      <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                        Meu Perfil
                      </h1>
                      <button
                        onClick={() => navigateWithUrl('home')}
                        className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
                      >
                        Voltar
                      </button>
                    </header>

                    <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] max-w-2xl mx-auto">
                      <div className="flex flex-col items-center mb-6">
                        <div className="w-24 h-24 rounded-full bg-violet-200 flex items-center justify-center mb-4">
                          <svg className="w-12 h-12 text-violet-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                          </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800">{user?.nome || 'Nome do Usuário'}</h2>
                        <p className="text-gray-600">Usuário Ativo</p>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome
                          </label>
                          <div className="flex">
                            <input
                              type="text"
                              value={user?.nome || 'Nome do Usuário'}
                              className="flex-grow p-3 bg-gray-100 border border-gray-300 rounded-l-lg"
                              readOnly
                            />
                            <button
                              className="bg-violet-100 text-violet-700 font-semibold px-4 py-2 rounded-r-lg hover:bg-violet-200 transition-colors"
                              onClick={() => setShowEditNameModal(true)}
                            >
                              Editar
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Você pode editar seu nome 1 vez por semana.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            E-mail
                          </label>
                          <div className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg">
                            {user?.email || 'email@exemplo.com'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Senha
                          </label>
                          <div className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg flex justify-between items-center">
                            <span>••••••••</span>
                            <button
                              className="text-violet-600 hover:text-violet-800 font-medium text-sm"
                              onClick={() => setShowChangePasswordModal(true)}
                            >
                              Alterar senha
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Botão para tornar-se administrador */}
                      <div className="mt-6 text-center">
                        <button
                          onClick={handleToggleAdmin}
                          disabled={adminCheckPerformed}
                          className={`${adminCheckPerformed ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'} text-gray-800 font-medium px-4 py-2 rounded-lg transition-all duration-300`}
                        >
                          {adminCheckPerformed ? 'Verificação realizada' : 'Mudar para administrador'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>;
      case 'politica-privacidade':
        return <PoliticaPrivacidade onVoltar={() => navigateWithUrl('home')} />;
      case 'cart':
        return <CartPage onVoltar={() => navigateWithUrl('home')} />;
      case 'product-detail':
        return <ProductDetailPage productId={currentParams?.id} onVoltar={() => navigateWithUrl('home')} />;
      case 'classroom':
        return <CoursePlayerPage onVoltar={() => navigateWithUrl('home')} />;
      case 'cronograma-personalizado':
        return <CronogramaPersonalizadoPage onVoltar={navigateWithUrl} />;
      case 'indique-ganhe':
        return <IndiqueGanhePage onVoltar={navigateWithUrl} />;
      case 'rewards-store':
        return <RewardsStorePage onVoltar={navigateWithUrl} />;
      case 'indique-e-ganhe':
        return <IndiqueGanhePage onVoltar={navigateWithUrl} />;
      case 'loja-de-recompensa':
        return <RewardsStorePage onVoltar={navigateWithUrl} />;
      case 'venda-premium':
        return <VendaPremiumPage onVoltar={navigateWithUrl} />;
      default:
        return <HomePage user={user} isAdmin={isAdmin} onNavigate={navigateWithUrl} onLogout={handleLogout} />;
    }
  };

  return (
    <UserProvider>
      <CartProvider>
        <div>
          {isLoggedIn ? renderPage() : <LandingPage onLogin={handleLogin} />}
        </div>
      </CartProvider>
    </UserProvider>
  );
}

export default App;
